<template>
  <div class="notes-timeline">
    <section
      v-if="hasPinnedNotes"
      class="notes-timeline__section mb-4"
    >
      <div class="d-flex flex-wrap align-items-center justify-content-between mb-2">
        <h5 class="mb-0">
          Pinned Notes
        </h5>
      </div>
      <div class="notes-list">
        <timeline-item
          v-for="note in filteredPinnedNotes"
          :key="note.id"
          :value="note"
          :show-reference="referenceType !== note.reference_type || referenceId !== note.reference_id"
          @on-destroy="() => handleNoteDestroyed(note, true)"
          @on-unpinned="(updatedNote) => handleNoteUnpinned(updatedNote)"
        />
      </div>
    </section>

    <section
      v-if="showTimelineForm"
      class="notes-timeline__section"
    >
      <timeline-form
        :create-func="createFunc"
        :reference-id="referenceId"
        :quick-note-options="quickNoteOptions"
        @on-save="handleNoteAdded"
        @on-hide="hideTimelineForm"
      />
    </section>

    <section class="notes-timeline__section">
      <div class="d-flex flex-wrap align-items-center justify-content-between mb-0">
        <b-button
          v-if="!hideNewNoteButton && !showTimelineForm"
          :size="newNoteButtonSize"
          variant="outline-secondary"
          @click="toggleTimelineForm"
        >
          <i class="fa fa-plus mr-1" />
          New Note
        </b-button>
      </div>

      <div
        v-if="loaded && !notes.length"
        class="text-muted mb-3"
      >
        No notes have been added yet.
      </div>

      <div
        v-for="group in groups"
        :key="group.key"
        class="mb-4"
      >
        <div class="d-flex align-items-center mb-3">
          <span class="notes-date-badge badge badge-pill text-primary font-w600 font-size-sm text-nowrap">
            {{ group.label }}
          </span>
          <div class="flex-grow-1 border-top ml-3" />
        </div>

        <div class="notes-list">
          <timeline-item
            v-for="note in group.notes"
            :key="note.id"
            :value="note"
            :show-reference="referenceType !== note.reference_type || referenceId !== note.reference_id"
            @on-destroy="() => handleNoteDestroyed(note)"
            @on-pinned="(updatedNote) => handleNotePinned(updatedNote)"
          />
        </div>
      </div>

      <div
        v-if="hasMore"
        class="text-center mt-2"
      >
        <b-button
          variant="outline-secondary"
          :disabled="isLoadingData"
          @click="handleShowMore"
        >
          {{ isLoadingData ? 'Loading...' : 'Show More Notes' }}
        </b-button>
      </div>
    </section>
  </div>
</template>

<script>
import { isEqual as _isEqual, isUndefined as _isUndefined, orderBy as _orderBy } from 'lodash';
import { DateTime } from 'luxon';
import paginationMixin from '@/mixins/paginationMixin';
import { WORK_ORDER_NOTE_TEMPLATES } from '@/constants/workOrderNoteTemplates';
import TimelineItem from '@/components/admin/notes/TimelineItem.vue';
import TimelineForm from '@/components/admin/notes/TimelineForm.vue';

export default {
  name: 'NotesTimeline',
  mixins: [paginationMixin],
  components: {
    TimelineItem,
    TimelineForm,
  },
  props: {
    referenceId: {
      type: Number,
      required: true,
    },
    referenceType: {
      type: String,
      required: true,
    },
    listFunc: {
      type: Function,
      required: true,
    },
    listParams: {
      type: Object,
      default: () => new Object(),
    },
    createFunc: {
      type: Function,
      required: true,
    },
    appendToQuery: {
      type: Boolean,
      default: () => false,
    },
    newNoteButtonSize: {
      type: String,
      default: 'sm',
    },
    hideNewNoteButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loaded: false,
      isLoadingData: false,
      pagination: {
        currentPage: 1,
        perPage: 5,
      },
      totalRows: 0,
      notes: [],
      pinnedNotes: [],
      showTimelineForm: false,
    };
  },
  methods: {
    dateLabel(date) {
      const today = DateTime.local().startOf('day');
      const daysAgo = Math.round(today.diff(date.startOf('day'), 'days').days);

      if (daysAgo === 0) {
        return 'Today';
      }
      if (daysAgo === 1) {
        return 'Yesterday';
      }
      return date.toFormat(date.year === today.year ? 'cccc, LLL d' : 'LLL d, yyyy');
    },
    updateTotalRows(newTotal) {
      this.totalRows = newTotal;
      this.$emit('total-rows-changed', newTotal + this.pinnedNotes.length);
    },
    handleShowMore() {
      if (this.isLoadingData || !this.hasMore) {
        return;
      }
      this.pagination = { ...this.pagination, currentPage: this.pagination.currentPage + 1 };
    },
    handleNoteAdded(newNote) {
      this.notes = [newNote, ...this.notes];
      this.updateTotalRows(this.totalRows + 1);
      this.showTimelineForm = false;
    },
    toggleTimelineForm() {
      this.showTimelineForm = !this.showTimelineForm;
    },
    hideTimelineForm() {
      this.showTimelineForm = false;
    },
    handleNoteDestroyed(note, pinned = false) {
      const noteIndex = (pinned ? this.pinnedNotes : this.notes).findIndex((currentNote) => currentNote.id === note.id);
      if (noteIndex !== -1) {
        (pinned ? this.pinnedNotes : this.notes).splice(noteIndex, 1);
        if (pinned) {
            this.updateTotalRows(this.totalRows);
        } else {
          this.updateTotalRows(this.totalRows - 1);
        }
      }
    },
    handleNotePinned(note) {
      const noteIndex = this.notes.findIndex((currentNote) => currentNote.id === note.id);
      if (noteIndex !== -1) {
        this.notes.splice(noteIndex, 1);
        this.pinnedNotes = _orderBy([note, ...this.pinnedNotes], ['created_at'], ['desc']);
        this.updateTotalRows(this.totalRows - 1);
      }
    },
    handleNoteUnpinned(note) {
      const noteIndex = this.pinnedNotes.findIndex((currentNote) => currentNote.id === note.id);
      if (noteIndex !== -1) {
        this.pinnedNotes.splice(noteIndex, 1);
        this.notes = [..._orderBy([note, ...this.notes], ['created_at'], ['desc'])];
        this.updateTotalRows(this.totalRows + 1);
      }
    },
    getNotes() {
      this.isLoadingData = true;
      this.listFunc(this.referenceId, {
        ...this.listParams,
        page: this.pagination.currentPage,
        per_page: this.pagination.perPage,
        pinned: false,
      })
        .then((response) => {
          const { total, notes } = response;
          this.updateTotalRows(total);
          this.notes = [...this.notes, ...notes];
        })
        .finally(() => {
          this.loaded = true;
          this.isLoadingData = false;
        });
    },
    getPinnedNotes() {
      this.listFunc(this.referenceId, {
        ...this.listParams,
        paginate: false,
        pinned: true,
      })
        .then((response) => {
          const { notes } = response;
          this.pinnedNotes = notes;
        });
    },
  },
  computed: {
    groups() {
      const byDate = {};

      this.notes.forEach((note) => {
        const date = DateTime.fromISO(note.created_at);
        const key = date.toISODate();
        if (!byDate[key]) {
          byDate[key] = { key, label: this.dateLabel(date), notes: [] };
        }
        byDate[key].notes.push(note);
      });

      return Object.values(byDate);
    },
    hasMore() {
      return this.notes.length < this.totalRows;
    },
    filteredPinnedNotes() {
      return this.pinnedNotes;
    },
    hasPinnedNotes() {
      return this.filteredPinnedNotes.length > 0;
    },
    quickNoteOptions() {
      return this.referenceType === 'WorkOrder' ? WORK_ORDER_NOTE_TEMPLATES : [];
    },
  },
  watch: {
    'pagination.currentPage': function (newPage, oldPage) {
      if (!_isEqual(newPage, oldPage) && !_isUndefined(oldPage)) {
        this.getNotes();
      }
    },
  },
  mounted() {
    this.getPinnedNotes();
    this.getNotes();
  },
};
</script>

<style scoped>
.notes-timeline {
  margin-top: -0.5rem;
}

.notes-date-badge {
  background-color: rgba(13, 110, 253, 0.08);
  padding: 0.4rem 0.75rem;
}

.notes-list {
  position: relative;
}

.notes-list::before {
  content: '';
  position: absolute;
  top: 0.75rem;
  bottom: 1.5rem;
  left: 0.95rem;
  width: 2px;
  background: #e9ecef;
}
</style>
